// src/components/Sidebar.jsx
import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import SeriesMenu from "./SeriesMenu.jsx";
import { SERIES_STATE } from "../lib/seriesConfig.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { hasRole } from "../auth/roles.js";
import { fetchRekoOpenCount } from "../lib/rekoApi.js";
import { fetchPublicSeasons } from "../lib/publicDbApi.js";

function NotificationBadge({ count = 0 }) {
    const n = Number(count || 0);
    if (!n) return null;

    return (
        <span
            style={{
                marginLeft: 8,
                minWidth: 18,
                height: 18,
                padding: "0 6px",
                borderRadius: 999,
                background: "#e53935",
                color: "#fff",
                fontSize: 11,
                fontWeight: 800,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
            }}
        >
            {n}
        </span>
    );
}

export default function Sidebar() {
    const closeSidebar = () => document.body.classList.remove("sidebar-open");
    const { user, loading } = useAuth();

    const [adminOpen, setAdminOpen] = useState(false);
    const [userOpen, setUserOpen] = useState(false);
    const [orgaOpen, setOrgaOpen] = useState(false);
    const [rekoOpen, setRekoOpen] = useState(false);
    const [rekoOpenCount, setRekoOpenCount] = useState(0);
    const [gt3ArchiveSeasons, setGt3ArchiveSeasons] = useState([]);

    const canSeeAdmin = !loading && user && hasRole(user, "admin");
    const canSeeOrga =
        !loading &&
        user &&
        (
            hasRole(user, "admin") ||
            hasRole(user, "orga")
        );

    const canSeeUser =
        !loading &&
        user &&
        (
            hasRole(user, "user") ||
            hasRole(user, "admin") ||
            hasRole(user, "orga") ||
            hasRole(user, "reko") ||
            hasRole(user, "reko_lead")
        );

    // Admin sieht alle Frontend-Bereiche; ReKo Lead darf auch ohne separate reko-Rolle hinein.
    const canSeeReko =
        !loading &&
        user &&
        (
            hasRole(user, "admin") ||
            hasRole(user, "reko") ||
            hasRole(user, "reko_lead")
        );

    useEffect(() => {
        if (!canSeeReko) {
            setRekoOpenCount(0);
            return;
        }

        let active = true;

        async function loadCount() {
            try {
                const data = await fetchRekoOpenCount();

                if (!active) return;

                setRekoOpenCount(Number(data?.count || 0));
            } catch {
                if (!active) return;

                setRekoOpenCount(0);
            }
        }

        loadCount();

        const timer = setInterval(loadCount, 20000);

        return () => {
            active = false;
            clearInterval(timer);
        };
    }, [canSeeReko]);

    useEffect(() => {
        let active = true;

        (async () => {
            try {
                const data = await fetchPublicSeasons("gt3x");
                if (!active) return;
                const seasons = Array.isArray(data?.seasons) ? data.seasons : [];
                const archiveList = seasons.filter((s) => {
                    const status = String(s?.status || "").toLowerCase();
                    return status === "archived" || status === "completed";
                });
                setGt3ArchiveSeasons(archiveList);
            } catch {
                if (!active) return;
                setGt3ArchiveSeasons([]);
            }
        })();

        return () => {
            active = false;
        };
    }, []);

    return (
        <aside className="sidebar">
            {/* Brand / Logo (über gesamte Breite, gelber Hover) */}
            <NavLink
                to="/"
                className="brand brand--full brand--hover"
                onClick={closeSidebar}
                aria-label="Zur Startseite"
            >
                <img src="/landing/Racevision-Logo.svg" alt="RaceVision" />
            </NavLink>

            {/* Navigation */}
            <nav className="nav">
                <NavLink
                    to="/manager"
                    end
                    className="nav-a nav-a--primary"
                    onClick={closeSidebar}
                >
                    <span>Liga Manager</span>
                </NavLink>

                {canSeeAdmin && (
                    <div className="nav-admin">
                        <button
                            type="button"
                            className="nav-admin-title nav-admin-toggle"
                            onClick={() => setAdminOpen((v) => !v)}
                            aria-expanded={adminOpen}
                        >
                            <span>Admin</span>
                            <span>{adminOpen ? "▾" : "▸"}</span>
                        </button>

                        {adminOpen && (
                            <div className="nav-admin-links">
                                <NavLink
                                    to="/manager/admin"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Dashboard</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/drivers"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Fahrerverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/teams"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Teamverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/users"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Userverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/seasons"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Saisonverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/assets"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Asset Management</span>
                                </NavLink>

                                <div style={{ borderTop: "1px solid rgba(255,255,255,.14)", margin: "4px 0" }} />

                                <NavLink
                                    to="/manager/reko/incidents"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Incidents erfassen</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/results-import"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Results importieren</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/driver-class"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Driver Class</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/rulebook"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Regelwerk Sync</span>
                                </NavLink>

                                <div style={{ borderTop: "1px solid rgba(255,255,255,.14)", margin: "4px 0" }} />

                                <NavLink
                                    to="/manager/admin/import"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Standings Import</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/import-single"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Standings Import Single Class</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/admin/polls"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Votings</span>
                                </NavLink>
                            </div>
                        )}
                    </div>
                )}

                {canSeeOrga && (
                    <div className="nav-admin">
                        <button
                            type="button"
                            className="nav-admin-title nav-admin-toggle"
                            onClick={() => setOrgaOpen((v) => !v)}
                            aria-expanded={orgaOpen}
                        >
                            <span>Orga</span>
                            <span>{orgaOpen ? "▾" : "▸"}</span>
                        </button>

                        {orgaOpen && (
                            <div className="nav-admin-links">
                                <NavLink
                                    to="/manager/orga/drivers"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Fahrerverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/orga/teams"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Teamverwaltung</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/orga/incidents"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Incidents erfassen</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/orga/results"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Results importieren</span>
                                </NavLink>
                            </div>
                        )}
                    </div>
                )}

                {canSeeUser && (
                    <div className="nav-admin">
                        <button
                            type="button"
                            className="nav-admin-title nav-admin-toggle"
                            onClick={() => setUserOpen((v) => !v)}
                            aria-expanded={userOpen}
                        >
                            <span>User</span>
                            <span>{userOpen ? "▾" : "▸"}</span>
                        </button>

                        {userOpen && (
                            <div className="nav-admin-links">
                                <NavLink
                                    to="/manager/user"
                                    end
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Übersicht</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/user/profile"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Profil</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/user/registrieren"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Registrieren</span>
                                </NavLink>
                            </div>
                        )}
                    </div>
                )}

                {canSeeReko && (
                    <div className="nav-admin">
                        <button
                            type="button"
                            className="nav-admin-title nav-admin-toggle"
                            onClick={() => setRekoOpen((v) => !v)}
                            aria-expanded={rekoOpen}
                        >
                            <span>
                                ReKo
                                <NotificationBadge count={rekoOpenCount} />
                            </span>
                            <span>{rekoOpen ? "▾" : "▸"}</span>
                        </button>

                        {rekoOpen && (
                            <div className="nav-admin-links">
                                <NavLink
                                    to="/manager/reko"
                                    end
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>Übersicht</span>
                                </NavLink>

                                <NavLink
                                    to="/manager/reko/incidents"
                                    className="nav-a nav-a--sub"
                                    onClick={closeSidebar}
                                >
                                    <span>
                                        Incidents
                                        <NotificationBadge count={rekoOpenCount} />
                                    </span>
                                </NavLink>

                                <NavLink
                                    to="/manager/reko/knowledge"
                                    className="nav-a"
                                    onClick={closeSidebar}
                                >
                                    Wissensdatenbank
                                </NavLink>
                            </div>
                        )}
                    </div>
                )}

                <NavLink
                    to="/feedback"
                    className="nav-a"
                    onClick={closeSidebar}
                >
                    <span>Feedback</span>
                </NavLink>

                {/* GT3 Xperience */}
                <SeriesMenu
                    title="GT3 Xperience"
                    titleImg="/landing/GT3X-Liga-Logo.png"
                    titleImgAlt="GT3 Xperience"
                    inactive={!SERIES_STATE.gt3x.active}
                    defaultOpen={true}
                    items={[
                        { to: "/gt3x/overview", label: "Overview" },
                        { to: "/gt3x/schedule", label: "Schedule" },
                        {
                            label: "Anmeldung",
                            isGroup: true,
                            children: [
                                { to: "/gt3x/anmeldung/anmelden", label: "Anmelden" },
                                { to: "/gt3x/anmeldung/anmeldeliste", label: "Anmeldeliste" },
                            ],
                        },
                        { to: "/gt3x/streamer", label: "Streamer" },
                        { to: "/gt3x/rules", label: "Regelwerk" },
                        {
                            label: "Raceday",
                            isGroup: true,
                            children: [
                                { to: "/gt3x/briefing", label: "Briefing" },
                                { to: "/gt3x/rennabmeldung", label: "Rennabmeldung" },
                            ],
                        },
                        { to: "/gt3x/ergebnisse", label: "Ergebnisse" },
                        {
                            label: "Standings",
                            isGroup: true,
                            children: [
                                { to: "/gt3x/standings/pro",    label: <span className="pill pill--pro">PRO</span> },
                                { to: "/gt3x/standings/int",    label: <span className="pill pill--int">INT</span> },
                                { to: "/gt3x/standings/am",     label: <span className="pill pill--am">AM</span> },
                                { to: "/gt3x/standings/team",   label: <span className="pill pill--team">TEAM</span> },
                                { to: "/gt3x/standings/marken", label: <span className="pill pill--marke">MARKE</span> },
                            ],
                        },
                        {
                            label: "Vorfälle",
                            isGroup: true,
                            children: [
                                { to: "/gt3x/incidents", label: "Incidents" },
                                { to: "/gt3x/protest", label: "Protest" },
                                { to: "/gt3x/lizenz", label: "Fahrerlizenz" },
                            ],
                        },
                        {
                            label: "Archiv",
                            isGroup: true,
                            children: gt3ArchiveSeasons.length
                                ? gt3ArchiveSeasons.map((s) => ({
                                    to: `/gt3x/archiv/${s.season_key}`,
                                    label: s.display_name || s.season_key || `Season ${s.season_no || ""}`.trim(),
                                }))
                                : [{ to: "/gt3x/archiv/season1", label: "Season 1" }],
                        },
                    ]}
                    onNavigate={closeSidebar}
                />

                <SeriesMenu
                    title="Special Xperience"
                    titleImg="/landing/SpecialX-Liga-Logo.png"
                    titleImgAlt="Special Xperience"
                    inactive={!SERIES_STATE.specialx.active}
                    defaultOpen={false}
                    items={[
                        { to: "/specialx/overview", label: "Overview" },
                        { to: "/specialx/schedule", label: "Schedule" },
                        {
                            label: "Anmeldung",
                            isGroup: true,
                            children: [
                                { to: "/specialx/anmeldung/anmelden", label: "Anmelden" },
                                { to: "/specialx/anmeldung/anmeldeliste", label: "Anmeldeliste" },
                            ],
                        },
                        { to: "/specialx/streamer", label: "Streamer" },
                        { to: "/specialx/rules", label: "Regelwerk" },
                        { to: "/specialx/standings", label: "Standings" },
                        { to: "/specialx/results", label: "Ergebnisse " },
                    ]}
                    onNavigate={closeSidebar}
                />

                {/* Weitere Serie */}
                <SeriesMenu
                    title="GT Endu Xperience"
                    titleImg="/landing/EnduX-Liga-Logo.png"
                    titleImgAlt="GT Endu Xperience"
                    inactive={!SERIES_STATE.gtex.active}
                    items={[{ to: "/gtex", label: "Noch nicht vorhanden" }]}
                    onNavigate={closeSidebar}
                />

                <SeriesMenu
                    title="GT4 Xperience"
                    titleImg="/landing/GT4X-Liga-Logo.png"
                    titleImgAlt="GT4 Xperience"
                    inactive={!SERIES_STATE.gt4x.active}
                    items={[{ to: "/gt4x", label: "Noch nicht vorhanden" }]}
                    onNavigate={closeSidebar}
                />
            </nav>

            {/* ➜ Instagram Link */}
            <div className="sidebar-social">
                <a
                    href="https://www.instagram.com/racevision.gg/"
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-social-link"
                >
                    <img
                        src="/icons/instagram.svg"
                        alt="Instagram"
                        className="sidebar-social-icon"
                    />
                    <span>@racevision.gg</span>
                </a>

                <a
                    href="https://discord.gg/2AteU7suuX"
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-social-link"
                >
                    <img
                        src="/icons/Discord.png"
                        alt="Discord"
                        className="sidebar-social-icon"
                    />
                    <span>RaceVision</span>
                </a>

                <a
                    href="https://www.youtube.com/@RacevisionTV"
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-social-link"
                >
                    <img
                        src="/icons/Youtube.png"
                        alt="Youtube"
                        className="sidebar-social-icon"
                    />
                    <span>@RacevisionTV</span>
                </a>
            </div>

            {/* Donate / Buy me a coffee */}
            <div className="sidebar-donate">
                <a
                    href="https://buymeacoffee.com/racevision"
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-donate-link"
                >
                    <img
                        src="/assets/buy-coffee.png"
                        alt="Buy Me a Coffee"
                        className="sidebar-donate-icon"
                    />
                    <span>Buy us some fuel</span>
                </a>
            </div>

            {/* ➜ Footer-Logo unten */}
            <div className="sidebar-footer">
                <a
                    href="https://www.bavariansimtec.com"
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-footer-link"
                >
                    <img
                        src="/landing/Sidebar-Sponsor.svg"
                        alt="Powered by Bavarian SimTec"
                        className="sidebar-footer-logo"
                    />
                </a>
            </div>
        </aside>
    );
}
